import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../dialog/dialog';
import { Button } from '../button/button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  /** Cancel button label (e.g. "Cancel"). Required — no English fallback. */
  cancelLabel: string;
  variant?: 'destructive' | 'primary';
  loading?: boolean;
  /**
   * Disables the confirm button while the dialog stays open — for a
   * confirmation whose body collects something first (see `children`), e.g. a
   * rejection that requires a reason. Distinct from `loading`, which means
   * "the confirmed action is in flight" and renders a spinner.
   */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  /** Optional icon rendered in a colored circle above the title. */
  icon?: React.ReactNode;
  /** Background color for the icon circle. Defaults to red tint for destructive, accent tint for primary. */
  iconBg?: string;
  /** Optional data-testid placed on DialogContent (for E2E selectors). */
  testid?: string;
  /**
   * Optional extra content rendered between the description and the footer
   * buttons — e.g. a "Don't show this warning anymore" checkbox. Kept as a
   * generic slot (rather than a dedicated checkbox prop) so any confirmation
   * dialog can extend its body without a bespoke modal.
   */
  children?: React.ReactNode;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'primary',
  loading = false,
  confirmDisabled = false,
  onConfirm,
  icon,
  iconBg,
  testid,
  children,
}: ConfirmDialogProps) {
  const defaultIconBg =
    variant === 'destructive' ? 'rgba(239,68,68,0.10)' : 'rgba(79,70,229,0.10)';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={testid}
        className="max-w-sm"
        style={{
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow:
            '0 32px 80px -24px rgba(15,23,42,0.30),0 8px 24px -8px rgba(15,23,42,0.12)',
        }}
      >
        {icon && (
          <div
            className="mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: iconBg ?? defaultIconBg }}
          >
            {icon}
          </div>
        )}
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
        {children && <div className="mt-4">{children}</div>}
        <div
          className="-mx-6 -mb-6 mt-6 flex justify-end gap-3 rounded-b-[16px] px-6 py-4"
          style={{ background: 'rgba(241,245,249,0.6)' }}
        >
          <Button
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
